import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  where,
} from '@angular/fire/firestore';
import { COLLECTIONS } from '@core/core.constants';
import { Edition, Stage, ChatGroup, getStageTitle } from '@core/models/edition.model';
import { Booking } from '@core/models/booking.model';
import { Ferry, Hotel, ReservationItem, Stock } from '@core/models/reservation-item.model';
import { format, isSameDay } from 'date-fns';
import { BehaviorSubject, Observable, combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { AdminFirestoreService } from '../../services/admin-firestore.service';
import { User } from '@core/models/user.model';
import { Functions, httpsCallable } from '@angular/fire/functions';

/** Reservation item not linked to any hotel/ferry stage, with what the admin needs to decide what to do with it. */
export type OrphanReservationItem = ReservationItem & {
  /** Bookings with a line using this item. */
  bookings: Booking[];
  activeBookingsCount: number;
  /** Stages of the item's type (hotel/ferry) it can be moved to. */
  targetStages: { label: string; value: string; dateTime: Date }[];
};

@Injectable({
  providedIn: 'root',
})
export class EditionService {
  firestore = inject(AdminFirestoreService);
  functions = inject(Functions);
  private db = inject(Firestore);

  private editionId = new BehaviorSubject('');
  public editionId$ = this.editionId.asObservable();
  public edition$ = this.editionId$.pipe(
    switchMap((editionId: string) => this.getEdition(editionId)),
    shareReplay(1),
  );

  public editions$ = this.firestore
    .listDocumentsWithId<Edition>(COLLECTIONS.EDITIONS, orderBy('createdAt', 'desc'))
    .pipe(shareReplay(1));
  public users$ = this.firestore.listDocumentsWithId<User>(COLLECTIONS.USERS);
  public activeEditions$ = this.firestore.listDocumentsWithId<Edition>(
    COLLECTIONS.EDITIONS,
    orderBy('createdAt', 'desc'),
  );

  public reservationItems$ = this.editionId$.pipe(
    switchMap((editionId: string) =>
      this.firestore.listDocumentsWithId<ReservationItem>(
        COLLECTIONS.RESERVATION_ITEMS,
        where('editionId', '==', editionId),
      ),
    ),
    shareReplay(1),
  );

  hotels$ = this.firestore.listDocumentsWithId<Hotel>(COLLECTIONS.HOTELS).pipe(shareReplay(1));
  ferrys$ = this.firestore.listDocumentsWithId<Ferry>(COLLECTIONS.FERRYS).pipe(shareReplay(1));

  getReservationItemsByEdition(editionId: string) {
    return this.firestore.listDocumentsWithId<ReservationItem>(
      COLLECTIONS.RESERVATION_ITEMS,
      where('editionId', '==', editionId),
    );
  }

  getStagesReservationItems(editionId: string) {
    return combineLatest([
      this.getEditionStages(editionId),
      this.getReservationItemsByEdition(editionId),
      this.hotels$,
      this.ferrys$,
    ]).pipe(
      map(([allStages, allReservationItems, allHotels, allFerrys]) => {
        const filteredStages = allStages.filter((stage) => stage.hasFerry || stage.hasHotel);

        return filteredStages.map((stage) => {
          const reservationItems = allReservationItems.filter(
            (b) => b.date !== undefined && isSameDay(b.date, stage.dateTime),
          );

          // Filtrar y enriquecer los hoteles
          const hotels: ReservationItem[] = reservationItems
            .filter((a) => a.itemType === 'HOTEL')
            .map((hotelItem) => {
              const itemDetails = allHotels.find((hotel) => hotel.id === hotelItem.itemId) satisfies Hotel | undefined;
              return {
                ...hotelItem,
                itemDetails,
              };
            })
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

          // Filtrar y enriquecer los ferrys
          const ferrys: ReservationItem[] = reservationItems
            .filter((a) => a.itemType === 'FERRY')
            .map((ferryItem) => {
              const itemDetails = allFerrys.find((ferry) => ferry.id === ferryItem.itemId) satisfies Ferry | undefined;
              return {
                ...ferryItem,
                itemDetails,
              };
            })
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

          return {
            ...stage,
            hotels,
            ferrys,
          };
        });
      }),
    );
  }

  async createEdition(data: Omit<Edition, 'id'>, stages: Partial<Stage>[]): Promise<string> {
    const editionId = await this.firestore.create(COLLECTIONS.EDITIONS, this.prepareDataToSaveEdition(data));

    await Promise.all(
      stages.map(async (stage, index) => {
        await this.firestore.createOrUpdate(
          [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_STAGES].join('/'),
          stage.id ?? '',
          {
            ...this.prepareDataToSaveStage(stage),
            order: index + 1,
          },
        );
      }),
    );

    return editionId;
  }

  async updateEdition(data: Omit<Edition, 'id'>, editionId: string, stages: Partial<Stage>[]) {
    return await this.firestore
      .update(COLLECTIONS.EDITIONS, {
        ...this.prepareDataToSaveEdition(data),
        id: editionId,
      })
      .then(async (_) => {
        await Promise.all(
          stages.map((stage, index) =>
            this.firestore.createOrUpdate(
              [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_STAGES].join('/'),
              stage.id ?? '',
              {
                ...this.prepareDataToSaveStage(stage),
                order: index + 1,
              },
            ),
          ),
        );
      });
  }

  /**
   * Reservation items whose day has no hotel/ferry stage, usually because the stage date was changed afterwards.
   * They don't show up in any stage tab but the app may still offer them, so a human must move or delete them
   * (a new day may mean different hotels or room quotas, so they are never moved automatically).
   */
  getOrphanReservationItems(editionId: string): Observable<OrphanReservationItem[]> {
    return combineLatest([
      this.getEditionStages(editionId),
      this.getReservationItemsByEdition(editionId),
      this.firestore.listDocumentsWithId<Booking>(COLLECTIONS.BOOKINGS, where('editionId', '==', editionId)),
    ]).pipe(
      map(([stages, items, bookings]) => {
        const bookingStages = stages.filter((stage) => stage.hasHotel || stage.hasFerry);
        return items
          .filter((item) => !item.date || !bookingStages.some((stage) => isSameDay(item.date, stage.dateTime)))
          .map((item) => {
            const itemBookings = bookings.filter((booking) => booking.booking?.some((room) => room.itemId === item.id));
            return {
              ...item,
              bookings: itemBookings,
              activeBookingsCount: itemBookings.filter((booking) => booking.status !== 'CANCELLED').length,
              targetStages: bookingStages
                .filter((stage) => (item.itemType === 'HOTEL' ? stage.hasHotel : stage.hasFerry))
                .map((stage) => ({
                  label: `${format(stage.dateTime, 'dd/MM')} ${getStageTitle(stage, 'es')}`,
                  value: stage.id,
                  dateTime: stage.dateTime,
                })),
            };
          })
          .sort((a, b) => (a.date ? new Date(a.date).getTime() : 0) - (b.date ? new Date(b.date).getTime() : 0));
      }),
    );
  }

  /** Moves an item to another day keeping its stocks, and updates the date of the booking lines that use it. */
  moveReservationItemToDate(item: OrphanReservationItem, date: Date) {
    return Promise.all([
      this.updateReservationItem({ id: item.id, date }),
      ...item.bookings.map((booking) =>
        this.firestore.update<Booking>(COLLECTIONS.BOOKINGS, {
          id: booking.id,
          booking: booking.booking.map((room) =>
            room.itemId === item.id
              ? // Keep the stored format: numeric timestamp when saved from the app, Date when saved from admin.
                { ...room, itemDate: room.itemDate instanceof Date ? date : date.getTime() }
              : room,
          ),
        }),
      ),
    ]);
  }

  getEdition(editionId: string) {
    return this.firestore
      .getDocument<Edition>(COLLECTIONS.EDITIONS, editionId)
      .pipe(map((res) => this.prepareDates(res)));
  }

  getEditionStages(editionId: string) {
    return this.firestore
      .listDocumentsWithId<Stage>(
        [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_STAGES].join('/'),
        orderBy('order', 'asc'),
      )
      .pipe(map((res) => this.prepareStagesDates(res)));
  }

  saveHotelItem(data: Hotel) {
    return this.firestore.createOrUpdate(COLLECTIONS.HOTELS, data.id, data);
  }

  removeHotel(hotelId: string) {
    return this.firestore.deleteForever(COLLECTIONS.HOTELS, hotelId);
  }

  saveFerryItem(data: Ferry) {
    return this.firestore.createOrUpdate(COLLECTIONS.FERRYS, data.id, data);
  }

  removeFerry(ferryId: string) {
    return this.firestore.deleteForever(COLLECTIONS.FERRYS, ferryId);
  }

  createReservationItem(data: Partial<ReservationItem>) {
    return this.firestore.create(COLLECTIONS.RESERVATION_ITEMS, data);
  }

  updateReservationItem(data: Partial<ReservationItem>) {
    return this.firestore.update(COLLECTIONS.RESERVATION_ITEMS, data);
  }

  /** Bookings in these statuses hold stock, whether or not they expire (same rule as the app and the cron). */
  private readonly HOLDS_STOCK = ['PENDING', 'PAID'];

  /** Units of each stock of this item held by real bookings, counted from the bookings themselves. */
  private async bookedUnitsByStock(item: ReservationItem): Promise<Map<string, number>> {
    const bookingsSnap = await getDocs(
      query(collection(this.db, COLLECTIONS.BOOKINGS), where('editionId', '==', item.editionId)),
    );
    const booked = new Map<string, number>();
    for (const bookingDoc of bookingsSnap.docs) {
      const booking = bookingDoc.data() as Booking;
      if (!this.HOLDS_STOCK.includes(booking.status)) continue;
      for (const room of booking.booking ?? []) {
        if (room.itemId !== item.id) continue;
        booked.set(room.stockId, (booked.get(room.stockId) ?? 0) + room.quantity);
      }
    }
    return booked;
  }

  /** Quantity + availability of each stock, to detect the item changing while we recount bookings. */
  private stocksFingerprint(stocks: Stock[] = []): string {
    return JSON.stringify(stocks.map((s) => [s.id, s.quantity, s.available]));
  }

  /**
   * Saves an existing hotel/ferry edited in the admin form without losing the units already booked:
   * each stock's `available` is recomputed as new quantity − units held by real bookings (PENDING + PAID,
   * including the ones flagged as never expiring). Counting the bookings instead of deriving them from
   * quantity − available also repairs numbers that were already wrong, e.g. after an overbooking.
   * Throws `NOT_ENOUGH_STOCK` if the new quantity is below what is already booked.
   */
  async saveReservationItemKeepingBookings(item: ReservationItem): Promise<void> {
    const ref = doc(this.db, COLLECTIONS.RESERVATION_ITEMS, item.id);

    // A booking entering between the recount and the write would be overwritten, so check the stocks
    // didn't change in between and start over if they did.
    for (let attempt = 1; attempt <= 3; attempt++) {
      const beforeSnap = await getDoc(ref);
      const before = this.stocksFingerprint((beforeSnap.data() as ReservationItem | undefined)?.stocks);
      const booked = await this.bookedUnitsByStock(item);

      try {
        await runTransaction(this.db, async (transaction) => {
          const snap = await transaction.get(ref);
          const current = this.stocksFingerprint((snap.data() as ReservationItem | undefined)?.stocks);
          if (current !== before) throw new Error('STOCKS_CHANGED');

          const stocks = (item.stocks ?? []).map((stock) => {
            const held = booked.get(stock.id) ?? 0;
            const available = stock.quantity - held;
            if (available < 0) throw new Error(`NOT_ENOUGH_STOCK: ${stock.type} tiene ${held} reservadas`);
            return { ...stock, available };
          });

          transaction.update(ref, { ...item, stocks, updatedAt: new Date() });
        });
        return;
      } catch (err) {
        if ((err as Error).message !== 'STOCKS_CHANGED' || attempt === 3) throw err;
      }
    }
  }

  removeReservationItem(itemId: string) {
    return this.firestore.deleteForever(COLLECTIONS.RESERVATION_ITEMS, itemId);
  }

  removeStage(editionId: string, stageId: string) {
    return this.firestore.deleteForever(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_STAGES].join('/'),
      stageId,
    );
  }

  async toggleEnableBookings(editionId: string, enabled: boolean): Promise<void> {
    return this.firestore.update(COLLECTIONS.EDITIONS, { id: editionId, enableBookings: enabled });
  }

  private prepareDataToSaveEdition(data: Omit<Edition, 'id'>) {
    const dataToSave = {
      ...data,
      // Ensure mapUrl is explicitly included (can be null if removed)
      mapUrl: data.mapUrl ?? null,
      rates: data.rates.map((rate) => {
        return {
          ...rate,
          startDate: rate.startDate.getTime(),
          endDate: rate.endDate.getTime(),
        };
      }),
    };
    return dataToSave;
  }

  private prepareDates(edition: Edition) {
    const convertedEdition = {
      ...edition,
      rates: edition.rates.map((rate) => {
        return {
          ...rate,
          startDate: new Date(rate.startDate),
          endDate: new Date(rate.endDate),
        };
      }),
    };
    return convertedEdition;
  }

  private prepareDataToSaveStage(stage: Partial<Stage>) {
    const dataToSave = {
      ...stage,
      dateTime: stage.dateTime?.getTime(),
      // `title` es el campo legacy que siguen leyendo las versiones antiguas de la app:
      // se guarda siempre con el título en castellano.
      title: stage.titles?.es || stage.title || '',
    };
    return dataToSave;
  }

  prepareStagesDates(stages: Stage[]) {
    stages.map((stage) => {
      stage.dateTime = new Date(stage.dateTime);
    });
    return stages;
  }

  setEditionId(editionId: string) {
    this.editionId.next(editionId);
  }

  // Chat Groups methods
  getChatGroups(editionId: string) {
    return this.firestore.listDocumentsWithId<ChatGroup>(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_CHAT_GROUPS].join('/'),
    );
  }

  async createChatGroup(editionId: string, chatGroup: Partial<ChatGroup>) {
    return this.firestore.create(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_CHAT_GROUPS].join('/'),
      chatGroup,
    );
  }

  async updateChatGroup(editionId: string, chatGroup: Partial<ChatGroup>) {
    return this.firestore.update(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_CHAT_GROUPS].join('/'),
      chatGroup,
    );
  }

  async deleteChatGroup(editionId: string, chatGroupId: string) {
    return this.firestore.deleteForever(
      [COLLECTIONS.EDITIONS, editionId, COLLECTIONS.EDITION_CHAT_GROUPS].join('/'),
      chatGroupId,
    );
  }

  createUserAndAuth(email: string) {
    const callable = httpsCallable(this.functions, 'createUserAndAuth');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return callable({ email }).then((result: any) => result.data);
  }
}
